import { FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ModalType, ObjectFieldDTO } from 'src/types'
import { ModalWrapper } from '@microservices/wiskey-react-components'
import { HighlightOff, InfoOutlined } from '@mui/icons-material'
import { Box, DialogContent, IconButton, Tooltip, useTheme } from '@mui/material'

import { EditorBase } from '@components/ScriptValueDialog/EditorBase'

import { ErrorValidJSType } from '@helpers'

import 'react-resizable/css/styles.css'
import 'prismjs/themes/prism.css'

type ScriptValueDialogProps = {
  isShow: boolean
  onClose: (value?: boolean) => void
  value?: string
  title?: string
  btnText?: string
  errorMessage?: string
  objectFields?: ObjectFieldDTO[]
  modalType?: ModalType
  language?: 'js' | 'json' | 'jsx' | 'handlebars'
  isJsx?: boolean
  onSave?: (value: string) => void
  noValidate?: boolean
  hint?: string
  windowBoundaries?: boolean
  validator?: (value: string) => ErrorValidJSType | undefined
}

export const ScriptValueDialog: FC<ScriptValueDialogProps> = ({
  isShow,
  onClose,
  btnText,
  modalType,
  objectFields,
  title,
  isJsx,
  value,
  onSave,
  noValidate = false,
  hint,
  windowBoundaries = false,
  validator,
  language = 'jsx',
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const [invalid, setInvalid] = useState(false)
  const [valueJs, setValueJs] = useState(value || '')
  const isDirty = value !== valueJs
  const [tooltipOpen, setTooltipOpen] = useState(false)

  const handleSetInvalid = (state: boolean) => {
    setInvalid(state)
  }

  const getDialogErrorStyle = useMemo(() => {
    return {
      minWidth: 450,
      border: `1px solid ${theme.palette.grey[600]}`,
      borderColor: invalid ? theme.palette.error.light : theme.palette.grey[600],
      transition: 'all .5s ease-in-out',
    }
  }, [invalid, theme])

  const handleSaveChangedValues = (): void => {
    if (value !== valueJs) {
      onSave?.(valueJs)
    }
    handleTooltipClose()
    if (onClose) {
      onClose()
    }
  }

  const handleClose = () => {
    handleTooltipClose()
    if (onClose) {
      onClose()
    }
  }

  const handleTooltipClose = () => {
    setTooltipOpen(false)
  }

  const handleTooltipToggle = () => {
    setTooltipOpen(prevState => !prevState)
  }

  return (
    <ModalWrapper
      btnText={btnText || t(`modal.applyBtnText`)}
      disabledSave={!isDirty || invalid}
      focusCancel={false}
      focusSave={false}
      isShow={isShow}
      title={title || `${t(`addDialog.${modalType}`)} JS | JSX`}
      containerStyle={{
        minWidth: 450,
        maxWidth: '95vw',
        minHeight: 200,
        maxHeight: '95vh',
        overflow: 'auto',
      }}
      onClose={handleClose}
      onSave={handleSaveChangedValues}
    >
      <>
        <DialogContent>
          <Box>
            <EditorBase
              invalid={invalid}
              isJsx={isJsx}
              language={language}
              noValidate={noValidate}
              objectFields={objectFields}
              style={getDialogErrorStyle}
              validator={validator}
              value={value}
              onChange={value => setValueJs(value)}
              onInvalid={handleSetInvalid}
            />
            {hint && (
              <Tooltip
                disableFocusListener
                disableHoverListener
                disableTouchListener
                open={tooltipOpen}
                placement={'bottom'}
                title={hint}
                componentsProps={{
                  tooltip: {
                    sx: {
                      whiteSpace: 'pre-wrap',
                    },
                  },
                }}
                onClose={handleTooltipClose}
              >
                <IconButton onClick={handleTooltipToggle}>
                  {tooltipOpen ? <HighlightOff /> : <InfoOutlined />}
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </DialogContent>
      </>
    </ModalWrapper>
  )
}
